import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { reportsAPI, employeeAPI, departmentAPI } from '../../api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { ScrollArea } from '../../components/ui/scroll-area';
import { FileText, MapPin, Monitor, Clock, AlertTriangle, Users, DollarSign, TrendingUp, Download, ExternalLink } from 'lucide-react';
import { format, subDays } from 'date-fns';

const ReportsPage = () => {
  const { user } = useAuth();
  const companyId = user?.company_id;
  
  const [activeTab, setActiveTab] = useState('audit');
  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  
  // Filter states
  const [startDate, setStartDate] = useState(format(subDays(new Date(), 30), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [selectedEmployee, setSelectedEmployee] = useState('all');
  const [selectedEventType, setSelectedEventType] = useState('all');
  const [selectedDepartment, setSelectedDepartment] = useState('all');
  
  // Report data states
  const [auditData, setAuditData] = useState([]);
  const [gpsData, setGpsData] = useState([]);
  const [deviceData, setDeviceData] = useState([]);
  const [otData, setOtData] = useState([]);
  const [otSummary, setOtSummary] = useState(null);

  const loadBaseData = useCallback(async () => {
    if (!companyId) return;
    try {
      const [empRes, deptRes] = await Promise.all([
        employeeAPI.getAll(companyId),
        departmentAPI.getAll(companyId)
      ]);
      setEmployees(empRes.data);
      setDepartments(deptRes.data);
    } catch (error) {
      console.error('Error loading base data:', error);
    }
  }, [companyId]);

  useEffect(() => {
    loadBaseData();
  }, [loadBaseData]);

  const loadAuditReport = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const params = {
        company_id: companyId,
        start_date: startDate,
        end_date: endDate,
        limit: 500
      };
      if (selectedEmployee !== 'all') params.employee_id = selectedEmployee;
      if (selectedEventType !== 'all') params.event_type = selectedEventType;
      
      const res = await reportsAPI.getAuditReport(params);
      setAuditData(res.data);
    } catch (error) {
      console.error('Error loading audit report:', error);
    } finally {
      setLoading(false);
    }
  }, [companyId, startDate, endDate, selectedEmployee, selectedEventType]);

  const loadGpsReport = async () => {
    setLoading(true);
    try {
      const res = await reportsAPI.getGpsExceptions(companyId, startDate, endDate);
      setGpsData(res.data);
    } catch (error) {
      console.error('Error loading GPS report:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadDeviceReport = async () => {
    setLoading(true);
    try {
      const res = await reportsAPI.getDeviceIpReport(companyId, startDate, endDate);
      setDeviceData(res.data);
    } catch (error) {
      console.error('Error loading device report:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadOtReport = async () => {
    setLoading(true);
    try {
      const deptId = selectedDepartment !== 'all' ? selectedDepartment : null;
      const [reportRes, summaryRes] = await Promise.all([
        reportsAPI.getOvertimeReport(companyId, startDate, endDate, deptId),
        reportsAPI.getOvertimeSummary(companyId, startDate, endDate)
      ]);
      setOtData(reportRes.data);
      setOtSummary(summaryRes.data);
    } catch (error) {
      console.error('Error loading OT report:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    // Load data based on tab
    if (tab === 'audit') loadAuditReport();
    else if (tab === 'gps') loadGpsReport();
    else if (tab === 'device') loadDeviceReport();
    else if (tab === 'overtime') loadOtReport();
  };

  useEffect(() => {
    if (companyId && activeTab === 'audit') {
      loadAuditReport();
    }
  }, [companyId, activeTab, loadAuditReport]);

  const eventTypes = [
    'clock_in', 'clock_out', 'leave_requested', 'leave_approved', 
    'leave_rejected', 'employee_created', 'shift_assigned', 'bulk_shift_assigned'
  ];

  const getEventBadgeVariant = (eventType) => {
    if (eventType.includes('approved')) return 'success';
    if (eventType.includes('rejected')) return 'destructive';
    if (eventType.includes('clock')) return 'default';
    return 'secondary';
  };

  const getGoogleMapsLink = (lat, lng) => {
    if (lat && lng) {
      return `https://www.google.com/maps?q=${lat},${lng}`;
    }
    return null;
  };

  const handleExportCSV = async () => {
    try {
      const params = new URLSearchParams({
        start_date: startDate,
        end_date: endDate,
        limit: '5000'
      });
      if (companyId) params.append('company_id', companyId);
      if (selectedEmployee !== 'all') params.append('employee_id', selectedEmployee);
      if (selectedEventType !== 'all') params.append('event_type', selectedEventType);
      
      const response = await reportsAPI.exportAuditCSV(params.toString());
      
      // Create blob and download
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `audit_report_${startDate}_${endDate}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting CSV:', error);
    }
  };

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Reports & Analytics</h1>
          <p className="text-muted-foreground">View audit logs, GPS exceptions, device usage, and overtime reports</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-4">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="audit" className="gap-2" data-testid="tab-audit">
            <FileText className="h-4 w-4" />
            Audit Log
          </TabsTrigger>
          <TabsTrigger value="gps" className="gap-2" data-testid="tab-gps">
            <MapPin className="h-4 w-4" />
            GPS Exceptions
          </TabsTrigger>
          <TabsTrigger value="device" className="gap-2" data-testid="tab-device">
            <Monitor className="h-4 w-4" />
            Device & IP
          </TabsTrigger>
          <TabsTrigger value="overtime" className="gap-2" data-testid="tab-overtime">
            <Clock className="h-4 w-4" />
            Overtime
          </TabsTrigger>
        </TabsList>

        {/* Common Date Filters */}
        <Card>
          <CardContent className="pt-4">
            <div className="flex flex-wrap gap-4 items-end">
              <div className="space-y-2">
                <Label>Start Date</Label>
                <Input 
                  type="date" 
                  value={startDate} 
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-40"
                  data-testid="filter-start-date"
                />
              </div>
              <div className="space-y-2">
                <Label>End Date</Label>
                <Input 
                  type="date" 
                  value={endDate} 
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-40"
                  data-testid="filter-end-date"
                />
              </div>
              
              {activeTab === 'audit' && (
                <>
                  <div className="space-y-2">
                    <Label>Employee</Label>
                    <Select value={selectedEmployee} onValueChange={setSelectedEmployee}>
                      <SelectTrigger className="w-48" data-testid="filter-employee">
                        <SelectValue placeholder="All Employees" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Employees</SelectItem>
                        {employees.map((emp) => (
                          <SelectItem key={emp.id} value={emp.id}>
                            {emp.first_name} {emp.last_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Event Type</Label>
                    <Select value={selectedEventType} onValueChange={setSelectedEventType}>
                      <SelectTrigger className="w-48" data-testid="filter-event-type">
                        <SelectValue placeholder="All Events" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Events</SelectItem>
                        {eventTypes.map((type) => (
                          <SelectItem key={type} value={type}>
                            {type.replace(/_/g, ' ')}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}

              {activeTab === 'overtime' && (
                <div className="space-y-2">
                  <Label>Department</Label>
                  <Select value={selectedDepartment} onValueChange={setSelectedDepartment}>
                    <SelectTrigger className="w-48" data-testid="filter-department">
                      <SelectValue placeholder="All Departments" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Departments</SelectItem>
                      {departments.map((dept) => (
                        <SelectItem key={dept.id} value={dept.id}>
                          {dept.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <Button 
                onClick={() => handleTabChange(activeTab)} 
                disabled={loading}
                data-testid="apply-filters-btn"
              >
                {loading ? 'Loading...' : 'Apply Filters'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Audit Log Tab */}
        <TabsContent value="audit" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    Audit Log ({auditData.length} records)
                  </CardTitle>
                  <CardDescription>Track all system events and user actions with GPS coordinates</CardDescription>
                </div>
                <Button 
                  variant="outline" 
                  onClick={handleExportCSV}
                  className="gap-2"
                  data-testid="export-csv-btn"
                >
                  <Download className="h-4 w-4" />
                  Export CSV
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[500px]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Timestamp</TableHead>
                      <TableHead>User</TableHead>
                      <TableHead>Event</TableHead>
                      <TableHead>Entity</TableHead>
                      <TableHead>IP Address</TableHead>
                      <TableHead>GPS Location</TableHead>
                      <TableHead>Device</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {auditData.map((log) => {
                      const mapsLink = getGoogleMapsLink(log.latitude, log.longitude);
                      return (
                        <TableRow key={log.id} data-testid="audit-row">
                          <TableCell className="text-sm">
                            {format(new Date(log.timestamp), 'MMM d, yyyy h:mm a')}
                          </TableCell>
                          <TableCell className="font-medium">{log.user_name || 'Unknown'}</TableCell>
                          <TableCell>
                            <Badge variant={getEventBadgeVariant(log.event_type)}>
                              {log.event_type?.replace(/_/g, ' ')}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {log.entity_type}
                          </TableCell>
                          <TableCell className="text-sm font-mono">{log.ip_address || '-'}</TableCell>
                          <TableCell className="text-sm">
                            {log.latitude && log.longitude ? (
                              <div className="flex items-center gap-2">
                                <span className="font-mono text-xs">
                                  {log.latitude.toFixed(4)}, {log.longitude.toFixed(4)}
                                </span>
                                <a 
                                  href={mapsLink}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-primary hover:text-primary/80"
                                  title="View on Google Maps"
                                  data-testid="view-map-link"
                                >
                                  <ExternalLink className="h-4 w-4" />
                                </a>
                              </div>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </TableCell>
                          <TableCell className="text-sm max-w-[150px] truncate" title={log.device_info}>
                            {log.device_info?.split(' ')[0] || '-'}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {auditData.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                          No audit logs found for the selected filters
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        {/* GPS Exceptions Tab */}
        <TabsContent value="gps" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="h-5 w-5" />
                GPS/Location Exceptions ({gpsData.length} employees)
              </CardTitle>
              <CardDescription>Employees clocking in from multiple locations</CardDescription>
            </CardHeader>
            <CardContent>
              {gpsData.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <MapPin className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No GPS exceptions found for the selected period</p>
                  <p className="text-sm">Employees with multiple clock-in locations will appear here</p>
                </div>
              ) : (
                <ScrollArea className="h-[500px]">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Employee</TableHead>
                        <TableHead>Unique Locations</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {gpsData.map((item) => (
                        <TableRow key={item.employee_id} data-testid="gps-row">
                          <TableCell className="font-medium">{item.employee_name}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{item.unique_locations_count} locations</Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant="destructive" className="gap-1">
                              <AlertTriangle className="h-3 w-3" />
                              Exception
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Device & IP Tab */}
        <TabsContent value="device" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Monitor className="h-5 w-5" />
                Device & IP Usage Report ({deviceData.length} employees)
              </CardTitle>
              <CardDescription>Track devices and IP addresses used for clock-in/out</CardDescription>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[500px]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Employee</TableHead>
                      <TableHead>Unique IPs</TableHead>
                      <TableHead>Unique Devices</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {deviceData.map((item) => (
                      <TableRow key={item.employee_id} data-testid="device-row">
                        <TableCell className="font-medium">{item.employee_name}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{item.ip_count} IPs</Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{item.device_count} devices</Badge>
                        </TableCell>
                        <TableCell>
                          {item.ip_count > 3 || item.device_count > 3 ? (
                            <Badge variant="destructive">Review Needed</Badge>
                          ) : (
                            <Badge variant="secondary">Normal</Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                    {deviceData.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center text-muted-foreground py-8">
                          No device/IP data found for the selected period
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Overtime Tab */}
        <TabsContent value="overtime" className="space-y-4">
          {/* OT Summary Cards */}
          {otSummary && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-blue-100 rounded-lg">
                      <Clock className="h-6 w-6 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Total OT Hours</p>
                      <p className="text-2xl font-bold">{otSummary.total_ot_hours}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-green-100 rounded-lg">
                      <DollarSign className="h-6 w-6 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Estimated OT Cost</p>
                      <p className="text-2xl font-bold">₱{otSummary.total_ot_cost?.toLocaleString()}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-orange-100 rounded-lg">
                      <Users className="h-6 w-6 text-orange-600" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Employees with OT</p>
                      <p className="text-2xl font-bold">{otSummary.employees_with_ot}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-purple-100 rounded-lg">
                      <TrendingUp className="h-6 w-6 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Avg OT/Employee</p>
                      <p className="text-2xl font-bold">
                        {otSummary.employees_with_ot > 0 
                          ? (otSummary.total_ot_hours / otSummary.employees_with_ot).toFixed(1)
                          : 0}h
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Top OT Earners */}
          {otSummary?.top_earners?.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Top OT Earners</CardTitle>
                <CardDescription>Employees with highest overtime hours</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {otSummary.top_earners.map((earner, index) => (
                    <div key={earner.employee_id} className="flex items-center gap-4 p-3 bg-muted/50 rounded-lg">
                      <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold">
                        {index + 1}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium">{earner.employee_name}</p>
                        <p className="text-sm text-muted-foreground">{earner.department_name}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold">{earner.total_ot_hours}h</p>
                        <p className="text-sm text-muted-foreground">₱{earner.estimated_ot_cost?.toLocaleString()}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Full OT Report Table */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5" />
                Overtime Breakdown ({otData.length} employees)
              </CardTitle>
              <CardDescription>Detailed overtime report by employee</CardDescription>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[400px]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Employee</TableHead>
                      <TableHead>Department</TableHead>
                      <TableHead className="text-right">OT Hours</TableHead>
                      <TableHead className="text-right">Days with OT</TableHead>
                      <TableHead className="text-right">Est. Cost</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {otData.map((item) => (
                      <TableRow key={item.employee_id} data-testid="ot-row">
                        <TableCell className="font-medium">{item.employee_name}</TableCell>
                        <TableCell>{item.department_name}</TableCell>
                        <TableCell className="text-right">{item.total_ot_hours}h</TableCell>
                        <TableCell className="text-right">{item.days_with_ot} days</TableCell>
                        <TableCell className="text-right font-medium">
                          ₱{item.estimated_ot_cost?.toLocaleString()}
                        </TableCell>
                      </TableRow>
                    ))}
                    {otData.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                          No overtime data found for the selected period
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ReportsPage;
